<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Sorted Test Package Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading title="Sort Test Packages" class="mb-3">
      <template #right-content>
        <header-button-group :button_config="button_config" />
      </template>
    </basic-heading>
    <div>
      <b-alert show variant="info">
        <p>
          Test package ordering dictates how test packages are ordered when they
          are displayed to customers.
        </p>
        <hr />
        <p class="mb-0">
          Test package group ordering may be
          <a href="/#/testpackages/group-sort">updated here</a>.
        </p>
      </b-alert>
    </div>
    <template v-if="test_packages">
      <b-card
        no-body
        v-for="(groups, package_id) in test_packages"
        :key="package_id"
      >
        <b-card-header>
          <h5>{{ groups.name }}</h5>
        </b-card-header>
        <b-card-body>
          <div v-for="(group, index) in groups.groups" :key="index">
            <h6 class="mb-3">{{ group.name }}</h6>
            <sortable-list
              v-if="group.items && group.items.length"
              :list="group.items"
              :groupBy="`test_package_group_${index}`"
              keyField="id"
              labelField="name"
              @onOrderChange="onOrderChange"
              class="mb-4"
            />
          </div>
        </b-card-body>
      </b-card>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import SortableList from "@/components/shared/SortableList";
import { getCollection } from "@/shared/fetch-utils";
import { notificationOptions } from "@/shared/toast-notification-options";
import { testPackageNotifications } from "@/shared/notifications";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";

const LaboratoryRepository = LabRepositoryFactory.get("laboratory");

Vue.use(VueNotifications, notificationOptions);

export default {
  name: "TestPackageSort",
  components: {
    SortableList,
    BasicHeading,
    HeaderButtonGroup,
  },
  data() {
    return {
      loading: true,
      laboratory_id: this.$store.getters.getLaboratoryContext.id || null,
      test_package_collection: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/testpackages",
            title: "Test package list",
          },
        ],
      };
    },
    test_packages() {
      return this.test_package_collection
        ? this.test_package_collection.models.reduce((acc, test_package) => {
            const {
              id,
              name,
              sample_type_id,
              sample_type,
              test_package_group,
              test_package_group_id,
            } = test_package;

            if (id && sample_type_id && test_package_group_id && sample_type) {
              if (!acc[sample_type_id]) {
                acc[sample_type_id] = {
                  name: sample_type.name,
                  groups: {},
                };
              }

              if (!acc[sample_type_id]["groups"][test_package_group_id]) {
                acc[sample_type_id]["groups"][test_package_group_id] = {
                  name: test_package_group.name,
                  items: [],
                };
              }
              acc[sample_type_id]["groups"][test_package_group_id][
                "items"
              ].push({
                id,
                name,
              });
            }

            return acc;
          }, {})
        : null;
    },
  },
  methods: {
    async onOrderChange(sorted) {
      try {
        await LaboratoryRepository.sortTestPackages(this.laboratory_id, sorted);
      } catch ({ response }) {
        const { status, data } = response.response;

        this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
          title: "Server Error.",
          variant: "danger",
        });
      }
    },
  },
  async created() {
    this.loading = true;

    this.test_package_collection = await getCollection({
      collection_type: "test_package",
      filters: [
        {
          logic: "filterEquals",
          field: "laboratory_id",
          value: this.laboratory_id,
        },
      ],
      sort: [
        {
          logic: "sortAddAsc",
          field: "position",
        },
      ],
      message: this.$bvToast,
    });

    this.loading = false;
  },
  notifications: testPackageNotifications,
};
</script>
